import { useRef, useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { formatMessageTime, formatTime, getInitials } from '../utils/formatters';
import { isSameUser } from '../utils/userHelpers';
import { resolveMediaSrc } from '../utils/resolveMediaSrc';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';

const useMessages = ({
  socket,
  isConnected,
  currentUser,
  selectedUser,
  messages,
  setMessages,
  conversations,
  setConversations,
  allEmployees,
  userNamesCache,
  onlineUsers,
  messageInput,
  setMessageInput,
  t,
  setAlertState,
  // Reply context (WhatsApp-style): the message being replied to, owned by
  // MessengerPage so ChatInput can render the preview banner.
  replyingTo = null,
  clearReply = () => {},
}) => {
  const [hasMoreMessages, setHasMoreMessages] = useState({});
  const [messagesOffset, setMessagesOffset] = useState({});
  const [loadingMoreFor, setLoadingMoreFor] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messagesFullRef = useRef({});
  const [messagesLoadedCount, setMessagesLoadedCount] = useState({});
  const userAtBottomRef = useRef(true);
  const suppressAutoScrollRef = useRef(false);
  const forceScrollOnSelectRef = useRef(false);

  const loadMoreMessages = async (userId) => {
    if (loadingMoreFor === userId) {
      console.log('⚠️ Already loading messages for this user');
      return;
    }

    if (!hasMoreMessages[userId]) {
      console.log('⚠️ No more messages to load');
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) return;

    const oldScrollHeight = container.scrollHeight;
    const oldScrollTop = container.scrollTop;

    setLoadingMoreFor(userId);
    suppressAutoScrollRef.current = true;

    try {
      const currentMessages = messages[userId] || [];
      if (currentMessages.length === 0) {
        console.log('⚠️ No messages to use as reference');
        setLoadingMoreFor(null);
        suppressAutoScrollRef.current = false;
        return;
      }

      const oldestMessage = currentMessages[0];
      const beforeTimestamp = oldestMessage?.raw?.created_at || oldestMessage?.raw?.time || oldestMessage?.raw?.createdAt;

      if (!beforeTimestamp) {
        console.log('⚠️ No timestamp found in oldest message');
        setLoadingMoreFor(null);
        suppressAutoScrollRef.current = false;
        return;
      }

      const isGroup = isGroupRecipient(userId);
      const actualId = isGroup ? userId.replace('group-', '') : userId;
      const endpoint = isGroup 
        ? `${API_BASE_URL}/api/chat-groups/${actualId}/messages?limit=50&before=${beforeTimestamp}`
        : `${API_BASE_URL}/api/private-messages/messages/${actualId}?limit=50&before=${beforeTimestamp}`;

      const response = await apiFetch(endpoint, { method: 'GET' });

      if (!response.ok) throw new Error('Failed to fetch messages');
      const olderMessages = await response.json();

      const formattedMessages = (Array.isArray(olderMessages) ? olderMessages : (olderMessages.messages || olderMessages.data || [])).map((msg) => ({
        id: msg.id,
        text: msg.content,
        type: msg.type || 'text',
        mediaUrl: resolveMediaSrc(msg.mediaUrl || msg.mediaData),
        thumbnailUrl: resolveMediaSrc(msg.thumbnailUrl || msg.thumbnailData),
        mediaType: msg.mediaType,
        mediaName: msg.mediaName,
        imageWidth: msg.imageWidth,
        imageHeight: msg.imageHeight,
        mediaSize: msg.mediaSize,
        duration: msg.duration,
        time: formatMessageTime(msg.created_at || msg.time || msg.createdAt),
        sent: isSameUser(msg.senderId, currentUser?.infinityCode),
        status: msg.status || 'sent',
        replyToId: msg.replyToId || null,
        replyTo: msg.replyTo || null,
        reactions: msg.reactions || null,
        isForwarded: !!msg.isForwarded,
        raw: msg,
      }));

      const hasMore = formattedMessages.length === 50;

      setHasMoreMessages((prev) => ({ ...prev, [userId]: hasMore }));
      setMessagesOffset((prev) => ({ ...prev, [userId]: (prev[userId] || 0) + formattedMessages.length }));
      setMessages((prev) => ({
        ...prev,
        [userId]: [...formattedMessages, ...(prev[userId] || [])],
      }));

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - oldScrollHeight;
            container.scrollTop = oldScrollTop + scrollDiff;
          }
          suppressAutoScrollRef.current = false;
          setLoadingMoreFor(null);
        });
      });
    } catch (err) {
      console.error('❌ Failed to load more messages:', err);
      suppressAutoScrollRef.current = false;
      setLoadingMoreFor(null);
      alert('Failed to load older messages. Please try again.');
    }
  };

  const fetchMessagesForUser = async (userId) => {
    try {
      if (!isGroupRecipient(userId) && isSameUser(userId, currentUser?.infinityCode)) return;

      const isGroup = isGroupRecipient(userId);
      const actualId = isGroup ? userId.replace('group-', '') : userId;
      const endpoint = isGroup 
        ? `${API_BASE_URL}/api/chat-groups/${actualId}/messages?limit=100`
        : `${API_BASE_URL}/api/private-messages/messages/${actualId}?limit=100`;

      const response = await apiFetch(endpoint, { method: 'GET' });

      if (!response.ok) throw new Error('Failed to fetch messages');
      const messagesData = await response.json();

      const formattedMessages = (Array.isArray(messagesData) ? messagesData : (messagesData.messages || messagesData.data || [])).map((msg) => ({
        id: msg.id,
        text: msg.content,
        type: msg.type || 'text',
        mediaUrl: resolveMediaSrc(msg.mediaUrl || msg.mediaData),
        thumbnailUrl: resolveMediaSrc(msg.thumbnailUrl || msg.thumbnailData),
        mediaType: msg.mediaType,
        mediaName: msg.mediaName,
        imageWidth: msg.imageWidth,
        imageHeight: msg.imageHeight,
        mediaSize: msg.mediaSize,
        duration: msg.duration,
        time: formatMessageTime(msg.created_at || msg.time || msg.createdAt),
        sent: isSameUser(msg.senderId, currentUser?.infinityCode),
        status: msg.status || 'sent',
        replyToId: msg.replyToId || null,
        replyTo: msg.replyTo || null,
        reactions: msg.reactions || null,
        isForwarded: !!msg.isForwarded,
        sender: {
          id: msg.senderId || msg.sender?.infinityCode,
          name: msg.sender?.sr || msg.sender?.name || `User ${msg.senderId}`
        },
        raw: msg,
      }));

      setMessages((prev) => ({ ...prev, [userId]: formattedMessages }));
      setHasMoreMessages((prev) => ({ ...prev, [userId]: formattedMessages.length === 100 }));
      setMessagesOffset((prev) => ({ ...prev, [userId]: formattedMessages.length }));
      messagesFullRef.current[userId] = formattedMessages;
      setMessagesLoadedCount((prev) => ({ ...prev, [userId]: formattedMessages.length }));

      if (socket && formattedMessages.some((m) => !m.sent)) {
        socket.emit('private_message:mark_read', { senderId: userId });
      }

      forceScrollOnSelectRef.current = true;
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  };

  const handleIncomingMessage = (data, user = currentUser) => {
    const normalizeParticipantId = (value) => {
      if (value === undefined || value === null) return null;
      const parsed = parseInt(String(value).trim(), 10);
      return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : null;
    };

    const senderId = normalizeParticipantId(
      data?.sender?.infinityCode || data?.senderInfinityCode || data?.senderCode || data?.senderId
    );

    const receiverId = normalizeParticipantId(
      data?.receiver?.infinityCode || data?.receiverInfinityCode || data?.recipientId || data?.receiverId
    );

    const myId = normalizeParticipantId(user?.infinityCode);
    const selectedChatId = normalizeParticipantId(selectedUser?.id);
    const isSentByMe = !!(senderId && myId && senderId === myId);

    const isGroupMsg = !!data.groupId;
    const groupId = isGroupMsg ? `group-${data.groupId}` : null;

    const chatPartnerId = isGroupMsg ? groupId : (isSentByMe ? (selectedChatId || receiverId) : senderId);

    if (!chatPartnerId) return;
    if (isSentByMe && receiverId && !isSameUser(receiverId, chatPartnerId)) return;
    if (isSameUser(chatPartnerId, user?.infinityCode)) return;

    // For group messages: if I sent it, ignore the socket echo.
    // The message was already added optimistically as a temp entry and confirmed via the API response.
    // The socket broadcast reaches the sender too (they're in the group room), causing duplication.
    if (isGroupMsg && isSentByMe) return;

    const isDuplicate = messages[chatPartnerId]?.some((m) => m.id === data.id);
    if (isDuplicate) return;

    const newMessage = {
      id: data.id || Date.now(),
      text: data.content,
      type: data.type || 'text',
      mediaUrl: resolveMediaSrc(data.mediaUrl || data.mediaData),
      thumbnailUrl: resolveMediaSrc(data.thumbnailUrl || data.thumbnailData),
      mediaType: data.mediaType,
      mediaName: data.mediaName,
      imageWidth: data.imageWidth,
      imageHeight: data.imageHeight,
      mediaSize: data.mediaSize,
      duration: data.duration,
      time: formatMessageTime(data.created_at || new Date().toISOString()),
      sent: isSentByMe,
      status: 'delivered',
      replyToId: data.replyToId || null,
      replyTo: data.replyTo || null,
      reactions: data.reactions || null,
      isForwarded: !!data.isForwarded,
      raw: {
        id: data.id,
        created_at: data.created_at || new Date().toISOString(),
        senderId,
        receiverId: isGroupMsg ? groupId : receiverId,
        groupId: data.groupId || null,
      },
      sender: {
        id: senderId,
        name: data.senderName || data.sender?.sr || data.sender?.name || `User ${senderId}`
      },
    };

    // Idempotent dedup INSIDE the updater so it sees the latest state. The
    // previous closure-based check (`messages[chatPartnerId]?.some(...)`)
    // read stale state — when several socket paths (NotificationContext
    // bridge, messenger socket, DashboardManager listener) deliver the same
    // event in the same tick, every closure saw the pre-update list and
    // every setter appended the message. Comparing inside `prev` makes
    // multiple calls collapse to one append.
    let appended = false;
    setMessages((prev) => {
      const list = prev[chatPartnerId] || [];
      
      // 1. If the message ID is already in the list, do nothing
      if (list.some((m) => String(m.id) === String(newMessage.id))) return prev;
      
      // 2. If this is a message sent by me, check if there is a matching optimistic temp message
      if (isSentByMe) {
        const tempIndex = list.findIndex((m) => 
          String(m.id).startsWith('temp-') && 
          String(m.text).trim() === String(newMessage.text).trim()
        );
        if (tempIndex !== -1) {
          // Replace the temp message with the real socket message in place
          const updatedList = [...list];
          updatedList[tempIndex] = newMessage;
          return { ...prev, [chatPartnerId]: updatedList };
        }
      }
      
      // 3. Otherwise, append the message
      appended = true;
      return { ...prev, [chatPartnerId]: [...list, newMessage] };
    });
    if (!appended) return;

    setMessagesOffset((prev) => ({
      ...prev,
      [chatPartnerId]: (prev[chatPartnerId] || 0) + 1,
    }));

    setConversations((prev) => {
      const existingIndex = prev.findIndex((conv) => isSameUser(conv.id, chatPartnerId));
      const now = Date.now();
      const knownEmployee = allEmployees.find((emp) => isSameUser(emp.id, chatPartnerId));

      const userName = existingIndex >= 0
        ? prev[existingIndex].name
        : knownEmployee?.name ||
          userNamesCache[chatPartnerId] ||
          (!isSentByMe ? (data?.sender?.sr || data?.senderName) : null) ||
          (isSentByMe && selectedUser && isSameUser(selectedUser.id, chatPartnerId) ? selectedUser.name : null) ||
          `User ${chatPartnerId}`;

      const userNameEn = existingIndex >= 0
        ? (userNamesCache[chatPartnerId] || userName)
        : knownEmployee?.name ||
          userNamesCache[chatPartnerId] ||
          (!isSentByMe ? (data?.sender?.sr || data?.senderName) : null) ||
          (isSentByMe && selectedUser && isSameUser(selectedUser.id, chatPartnerId) ? selectedUser.name : null) ||
          `User ${chatPartnerId}`;

      let previewText;
      if (data.content) {
        previewText = data.content;
      } else {
        switch (data.type) {
          case 'image': previewText = '📷 Image'; break;
          case 'voice': previewText = '🎤 Voice Message'; break;
          case 'pdf': previewText = '📄 PDF Document'; break;
          case 'excel': previewText = '📊 Excel File'; break;
          case 'word': previewText = '📝 Word Document'; break;
          case 'powerpoint': previewText = '📽️ PowerPoint'; break;
          default: previewText = 'Message';
        }
      }

      const updatedConv = {
        // Spread existing conv first to preserve isGroup, isArchived, members, etc.
        ...(existingIndex >= 0 ? prev[existingIndex] : {}),
        id: chatPartnerId,
        name: userName,
        avatar: existingIndex >= 0 ? prev[existingIndex].avatar : getInitials(userNameEn),
        lastMessage: previewText,
        isFromMe: isSentByMe,
        time: formatTime(data.created_at || new Date().toISOString(), t),
        timestamp: now,
        unread: (isSameUser(selectedUser?.id, chatPartnerId) || isSentByMe)
          ? 0
          : (existingIndex >= 0 ? prev[existingIndex].unread + 1 : 1),
        online: onlineUsers.has(chatPartnerId),
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated.splice(existingIndex, 1);
        return [updatedConv, ...updated];
      }

      return [updatedConv, ...prev];
    });

    if (isSameUser(selectedUser?.id, chatPartnerId) && !isSentByMe && socket) {
      setTimeout(() => {
        if (isGroupMsg) {
          const actualGroupId = chatPartnerId.replace('group-', '');
          socket.emit('chat_group:mark_read', { groupId: actualGroupId });
        } else {
          socket.emit('private_message:mark_read', { senderId: chatPartnerId });
        }
      }, 500);
    }
  };

  const isGroupRecipient = (userId) => typeof userId === 'string' && userId.startsWith('group-');

  const handleSendMessage = async () => {
    if (
      !messageInput.trim() ||
      !selectedUser ||
      (!socket && !isGroupRecipient(selectedUser.id)) ||
      (!isConnected && !isGroupRecipient(selectedUser.id))
    ) return;

    if (!selectedUser.id || !currentUser?.infinityCode) {
      alert(t.userInfoMissing);
      return;
    }

    if (isSameUser(selectedUser.id, currentUser?.infinityCode)) {
      alert(t.cannotMessageSelf);
      return;
    }

    const messageContent = messageInput.trim();
    const receiverId = selectedUser.id;
    setMessageInput('');

    const tempId = `temp-${Date.now()}`;
    const now = new Date();

    // Snapshot the reply context before clearing it so the optimistic bubble
    // renders the quoted preview immediately.
    const replyContext = replyingTo
      ? {
          replyToId: replyingTo.id,
          replyTo: {
            id: replyingTo.id,
            senderId: replyingTo.sent
              ? currentUser.infinityCode
              : (replyingTo.sender?.id || replyingTo.raw?.senderId || receiverId),
            content: replyingTo.text || null,
            type: replyingTo.type || 'text',
            mediaName: replyingTo.mediaName || null,
          },
        }
      : null;

    const tempMessage = {
      id: tempId,
      text: messageContent,
      type: 'text',
      time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      sent: true,
      status: 'sending',
      replyToId: replyContext?.replyToId || null,
      replyTo: replyContext?.replyTo || null,
      raw: {
        id: tempId,
        created_at: now.toISOString(),
        senderId: currentUser.infinityCode,
        receiverId,
      },
    };
    if (replyContext) clearReply();

    setMessages((prev) => ({
      ...prev,
      [receiverId]: [...(prev[receiverId] || []), tempMessage],
    }));

    setMessagesOffset((prev) => ({
      ...prev,
      [receiverId]: (prev[receiverId] || 0) + 1,
    }));

    try {
      if (isGroupRecipient(receiverId)) {
        const actualGroupId = receiverId.replace('group-', '');
        const response = await apiFetch(`${API_BASE_URL}/api/chat-groups/${actualGroupId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: messageContent,
            type: 'text',
            replyToId: replyContext?.replyToId || undefined,
          })
        });
        
        if (response.ok) {
          const resData = await response.json();
          setMessages((prev) => ({
            ...prev,
            [receiverId]: prev[receiverId].map((msg) =>
              msg.id === tempId
                ? {
                    ...msg,
                    id: resData.data.id,
                    status: 'sent',
                    raw: {
                      ...msg.raw,
                      id: resData.data.id,
                    },
                  }
                : msg
            ),
          }));
        } else {
          throw new Error('Failed to send group message');
        }
      } else {
        socket.emit(
          'private_message:send',
          {
            receiverId,
            content: messageContent,
            type: 'text',
            replyToId: replyContext?.replyToId || undefined,
          },
          (response) => {
            if (response?.success) {
              setMessages((prev) => ({
                ...prev,
                [receiverId]: prev[receiverId].map((msg) =>
                  msg.id === tempId
                    ? {
                        ...msg,
                        id: response.data.id,
                        status: 'sent',
                        raw: {
                          ...msg.raw,
                          id: response.data.id,
                        },
                      }
                    : msg
                ),
              }));
            } else {
              throw new Error(response?.error || 'Failed to send');
            }
          }
        );
      }

      setConversations((prev) => {
        const existingIndex = prev.findIndex((conv) => isSameUser(conv.id, receiverId));
        const timestamp = now.getTime();

        const updatedConv = {
          // Spread existing conv first to preserve isGroup, isArchived, members, etc.
          ...(existingIndex >= 0 ? prev[existingIndex] : {}),
          id: receiverId,
          name: selectedUser.name,
          avatar: selectedUser.avatar,
          lastMessage: messageContent,
          isFromMe: true,
          time: t.justNow,
          timestamp,
          unread: 0,
          online: onlineUsers.has(receiverId),
        };

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated.splice(existingIndex, 1);
          return [updatedConv, ...updated];
        }

        return [updatedConv, ...prev];
      });
    } catch (err) {
      setMessages((prev) => ({
        ...prev,
        [receiverId]: prev[receiverId].filter((msg) => msg.id !== tempId),
      }));

      setMessagesOffset((prev) => ({
        ...prev,
        [receiverId]: Math.max(0, (prev[receiverId] || 0) - 1),
      }));

      alert(t.sendFailed);
    }
  };

  // ── Emoji reactions ─────────────────────────────────────────────────────
  // Optimistic local toggle (WhatsApp semantics: one reaction per user) +
  // API call; the socket broadcast then carries the authoritative state to
  // every participant, including this client.
  const applyReactionLocally = (chatId, messageId, updater) => {
    setMessages((prev) => {
      const list = prev[chatId];
      if (!list) return prev;
      let changed = false;
      const updated = list.map((msg) => {
        if (String(msg.id) !== String(messageId)) return msg;
        changed = true;
        return { ...msg, reactions: updater(msg.reactions) };
      });
      return changed ? { ...prev, [chatId]: updated } : prev;
    });
  };

  const toggleReaction = async (message, emoji) => {
    if (!selectedUser || !currentUser?.infinityCode) return;
    const chatId = selectedUser.id;
    const myCode = String(currentUser.infinityCode);

    applyReactionLocally(chatId, message.id, (current) => {
      const reactions = { ...(current || {}) };
      const hadSame = Array.isArray(reactions[emoji]) && reactions[emoji].includes(myCode);
      for (const key of Object.keys(reactions)) {
        reactions[key] = (reactions[key] || []).filter((code) => code !== myCode);
        if (reactions[key].length === 0) delete reactions[key];
      }
      if (!hadSame) reactions[emoji] = [...(reactions[emoji] || []), myCode];
      return Object.keys(reactions).length ? reactions : null;
    });

    try {
      const isGroup = isGroupRecipient(chatId);
      const endpoint = isGroup
        ? `${API_BASE_URL}/api/chat-groups/${chatId.replace('group-', '')}/messages/${message.id}/reactions`
        : `${API_BASE_URL}/api/private-messages/messages/${message.id}/reactions`;
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      });
      if (!response.ok) throw new Error('Failed to toggle reaction');
      const { data } = await response.json();
      // Reconcile with the authoritative server state.
      applyReactionLocally(chatId, message.id, () => data?.reactions || null);
    } catch (err) {
      console.error('Reaction toggle failed:', err);
      // Revert by re-running the same toggle (it is its own inverse).
      applyReactionLocally(chatId, message.id, (current) => {
        const reactions = { ...(current || {}) };
        const hadSame = Array.isArray(reactions[emoji]) && reactions[emoji].includes(myCode);
        for (const key of Object.keys(reactions)) {
          reactions[key] = (reactions[key] || []).filter((code) => code !== myCode);
          if (reactions[key].length === 0) delete reactions[key];
        }
        if (!hadSame) reactions[emoji] = [...(reactions[emoji] || []), myCode];
        return Object.keys(reactions).length ? reactions : null;
      });
    }
  };

  // Socket event from either party (private) or any member (group).
  const handleReactionEvent = (data, user = currentUser) => {
    if (!data?.messageId) return;
    const myCode = String(user?.infinityCode || '');
    const chatId = data.groupId
      ? `group-${String(data.groupId).replace('group-', '')}`
      : (String(data.senderId) === myCode ? String(data.receiverId) : String(data.senderId));
    applyReactionLocally(chatId, data.messageId, () => data.reactions || null);
  };

  // ── Forward ─────────────────────────────────────────────────────────────
  // Server-side copy keeps media pointing at stable storage keys. After a
  // successful forward, refresh any already-loaded target thread so the
  // copies appear without a reload.
  const forwardMessage = async (message, targets) => {
    const sourceIsGroup = isGroupRecipient(selectedUser?.id);
    const response = await apiFetch(
      `${API_BASE_URL}/api/private-messages/messages/${message.id}/forward`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceType: sourceIsGroup ? 'group' : 'private',
          sourceGroupId: sourceIsGroup ? String(selectedUser.id).replace('group-', '') : undefined,
          targets: targets.map((conv) => ({
            type: isGroupRecipient(conv.id) ? 'group' : 'user',
            id: conv.id,
          })),
        }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Forward failed');
    }

    // Refresh loaded target threads + bump conversation previews.
    for (const conv of targets) {
      if (messages[conv.id]) {
        fetchMessagesForUser(conv.id).catch(() => {});
      }
    }
    setConversations((prev) => {
      const targetIds = new Set(targets.map((x) => String(x.id)));
      const preview = message.text
        || (message.type === 'image' ? '📷 Image'
          : message.type === 'voice' ? '🎤 Voice Message'
          : message.mediaName || 'Message');
      return prev.map((conv) =>
        targetIds.has(String(conv.id))
          ? { ...conv, lastMessage: preview, isFromMe: true, time: t.justNow, timestamp: Date.now() }
          : conv
      );
    });
  };

  const markMessagesAsRead = (userId) => {
    setMessages((prev) => ({
      ...prev,
      [userId]: (prev[userId] || []).map((msg) => ({
        ...msg,
        status: msg.sent ? 'read' : msg.status,
      })),
    }));
  };

  const handleTyping = () => {
    if (!socket || !selectedUser || isGroupRecipient(selectedUser.id)) return;

    socket.emit('private_message:typing', {
      recipientId: selectedUser.id,
      isTyping: true,
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('private_message:typing', {
        recipientId: selectedUser.id,
        isTyping: false,
      });
    }, 3000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleDeleteMessage = async (message) => {
    if (!message || !message.id) return;
    const isGroup = isGroupRecipient(selectedUser.id);
    const actualId = isGroup ? selectedUser.id.replace('group-', '') : selectedUser.id;

    try {
      let endpoint = '';
      if (isGroup) {
        endpoint = `${API_BASE_URL}/api/chat-groups/${actualId}/messages/${message.id}`;
      } else {
        endpoint = `${API_BASE_URL}/api/private-messages/messages/${message.id}`;
      }

      const response = await apiFetch(endpoint, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete message');
      }

      // Remove message from local state and update conversation preview atomically
      setMessages((prev) => {
        const chatId = selectedUser.id;
        const userMessages = prev[chatId] || [];
        const updatedMessages = userMessages.filter((m) => m.id !== message.id);

        // Update the conversation preview (lastMessage shown in sidebar)
        setConversations((convPrev) =>
          convPrev.map((conv) => {
            if (conv.id !== chatId) return conv;

            // Find the new last visible message
            const lastMsg = updatedMessages.length > 0 ? updatedMessages[updatedMessages.length - 1] : null;

            let lastMessageText = '';
            if (lastMsg) {
              if (lastMsg.text) {
                lastMessageText = lastMsg.text;
              } else {
                switch (lastMsg.type) {
                  case 'image': lastMessageText = '📷 Image'; break;
                  case 'voice': lastMessageText = '🎤 Voice Message'; break;
                  case 'pdf': lastMessageText = '📄 PDF Document'; break;
                  case 'excel': lastMessageText = '📊 Excel File'; break;
                  case 'word': lastMessageText = '📝 Word Document'; break;
                  case 'powerpoint': lastMessageText = '📽️ PowerPoint'; break;
                  default: lastMessageText = '';
                }
              }
            }

            return {
              ...conv,
              lastMessage: lastMessageText,
              isFromMe: lastMsg ? !!lastMsg.sent : conv.isFromMe,
              time: lastMsg ? lastMsg.time : conv.time,
            };
          })
        );

        return {
          ...prev,
          [chatId]: updatedMessages,
        };
      });
    } catch (err) {
      console.error('Delete message error:', err);
      if (setAlertState) {
        setAlertState({
          open: true,
          title: t.error || 'Error',
          message: err.message || t.sendFailed || 'Failed to delete message',
          variant: 'error',
          showCancel: false,
          onConfirm: null,
        });
      }
    }
  };

  return {
    hasMoreMessages,
    messagesOffset,
    loadingMoreFor,
    loadMoreMessages,
    fetchMessagesForUser,
    handleIncomingMessage,
    handleSendMessage,
    handleDeleteMessage,
    toggleReaction,
    handleReactionEvent,
    forwardMessage,
    markMessagesAsRead,
    handleTyping,
    handleKeyPress,
    messagesEndRef,
    messagesContainerRef,
    userAtBottomRef,
    forceScrollOnSelectRef,
    suppressAutoScrollRef,
    messagesFullRef,
    messagesLoadedCount,
  };
};

export default useMessages;